import React, { useState, useMemo } from 'react';
import { 
  Box, Button, TextField, Paper, Typography, Stack, 
  Divider, CircularProgress, List, ListItem, Chip, Fade 
} from '@mui/material';
import { PatternFormat } from 'react-number-format';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import { AgendamentoService } from '../../services/agendamentoservice';

export const StepConsulta: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [contato, setContato] = useState({ nome: '', celular: '' });
  const [agendamentos, setAgendamentos] = useState<any[] | null>(null);

  // Validação: Nome Completo (mínimo 2 palavras) e Celular (10 ou 11 dígitos)
  const isValido = useMemo(() => {
    const nomeLimpo = contato.nome.trim();
    const temNomeCompleto = nomeLimpo.split(/\s+/).length >= 2 && nomeLimpo.length >= 6;
    const apenasNumeros = contato.celular.replace(/\D/g, '');
    const celularValido = apenasNumeros.length >= 10 && apenasNumeros.length <= 11;
    return temNomeCompleto && celularValido;
  }, [contato]);

  const handleConsultar = async () => {
    setLoading(true);
    setAgendamentos(null);

    const payload = {
      nome: contato.nome.trim(),
      celular: contato.celular.replace(/\D/g, '')
    };

    try {
      const response = await AgendamentoService.consultar(payload);
      setAgendamentos(response as any || []); 
    } catch (error) {
      console.error("Erro na consulta:", error);
      alert("Erro ao buscar agendamentos. Verifique os dados e tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const renderSituacao = (situacao: string) => {
    switch (situacao) {
      case "AGUARDANDO":
        return <Chip label="Aguardando" color="warning" size="small" sx={{ fontWeight: 700 }} />;
      case "CONFIRMADO_EMPRESA":
      case "CONFIRMADO_AUTOMATICO":
      case "EXECUTADA":
        return <Chip label={situacao === "EXECUTADA" ? "Executada" : "Confirmado"} color="success" size="small" sx={{ fontWeight: 700 }} />;
      case "CANCELADO_EMPRESA":
      case "CANCELADO_CLIENTE":
        return <Chip label="Cancelado" color="error" size="small" sx={{ fontWeight: 700 }} />;
      default:
        return <Chip label="Pendente" color="default" size="small" sx={{ fontWeight: 700 }} />;
    }
  };

  // Formatação para o padrão ISO '2026-02-20T13:00:00Z'
  const formatarData = (dataIso: string) => {
    try {
      return new Date(dataIso).toLocaleString('pt-BR', {
        day: '2-digit', 
        month: 'long', 
        hour: '2-digit', 
        minute: '2-digit'
      });
    } catch (e) {
      return "Data indisponível";
    }
  };

  return (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 600, mx: 'auto', mt: 2 }}>
        <Paper sx={{ p: 4, borderRadius: 6, border: '1px solid rgba(255,255,255,0.05)', bgcolor: 'var(--color-surface)' }}>
          <Typography variant="h6" fontWeight={900} mb={1} display="flex" alignItems="center" gap={1} color="var(--color-text)">
            <SearchIcon sx={{ color: 'var(--color-primary)' }} /> Meus Agendamentos
          </Typography>
          <Typography variant="body2" color="var(--color-text)" sx={{ opacity: 0.6 }} mb={3}>
            Informe seus dados para localizar seus horários marcados.
          </Typography>

          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Nome Completo"
              variant="outlined"
              placeholder="Ex: João Silva"
              value={contato.nome}
              onChange={(e) => setContato({ ...contato, nome: e.target.value })}
              error={contato.nome.length > 3 && contato.nome.trim().split(/\s+/).length < 2}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />

            <PatternFormat
              format="(##) #####-####"
              mask="_"
              customInput={TextField}
              fullWidth
              label="Celular / WhatsApp"
              value={contato.celular}
              onValueChange={(v) => setContato({ ...contato, celular: v.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />

            <Button 
              fullWidth
              variant="contained" 
              size="large"
              disabled={!isValido || loading} 
              onClick={handleConsultar}
              sx={{ 
                borderRadius: 4, py: 1.8, fontWeight: 900, 
                bgcolor: 'var(--color-primary)',
                color: '#000',
                '&:hover': { bgcolor: 'var(--color-primary)', filter: 'brightness(0.9)' },
                '&.Mui-disabled': { opacity: 0.5, bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'LOCALIZAR AGENDAMENTOS'}
            </Button>
          </Stack>

          {agendamentos && (
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 3, opacity: 0.1 }} />
              <Typography variant="subtitle2" fontWeight={800} mb={2} color="var(--color-text)">
                {agendamentos.length > 0 ? 'Resultados encontrados:' : 'Nenhum agendamento encontrado.'}
              </Typography>
              
              <List disablePadding>
                {agendamentos.map((item, index) => (
                  <ListItem 
                    key={index}
                    sx={{ 
                      flexDirection: 'column', alignItems: 'stretch', mb: 2, p: 2,
                      borderRadius: 4, border: '1px solid rgba(255,255,255,0.05)', 
                      bgcolor: 'rgba(255,255,255,0.02)'
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                      <Typography fontWeight={900} variant="body1" color="var(--color-text)">
                        {item.produto?.descricao || item.descricaoServico || 'Serviço'}
                      </Typography>
                      {renderSituacao(item.situacaoAgenda)}
                    </Box>

                    <Stack spacing={1}>
                      <Typography variant="caption" display="flex" alignItems="center" gap={1} color="var(--color-text)" sx={{ opacity: 0.8 }}>
                        <CalendarMonthIcon sx={{ fontSize: 16, color: 'var(--color-primary)' }} />
                        {formatarData(item.inicio)}
                      </Typography>
                      <Typography variant="caption" display="flex" alignItems="center" gap={1} color="var(--color-text)" sx={{ opacity: 0.8 }}>
                        <PersonIcon sx={{ fontSize: 16, color: 'var(--color-primary)' }} />
                        Profissional: {item.nomeProfissional || 'Qualquer profissional'}
                      </Typography>
                    </Stack>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      </Box>
    </Fade>
  );
};